<script setup lang="ts">
import { computed, ref, inject, onMounted } from 'vue'
import BbelField from '@/views/builder/components/bbel/BbelField.vue'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'
import { ghostHeaderNames, type GhostHeaderRow } from './connection-preview'
import type { ScopeDescriptor } from '@/types/bbel-types'

const props = defineProps<{
  modelValue: Record<string, string[]>
  /** Headers the bound connection injects at run time — readonly rows above the user's own. */
  connectionHeaders?: GhostHeaderRow[]
  connectionName?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, string[]>]
}>()

// #5683 — header values are BBEL fields (scope provided by BuilderView; empty in read-only hosts).
const bbelScopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed<ScopeDescriptor>(() => bbelScopeCtx?.value ?? EMPTY_BBEL_SCOPE)

const entries = ref<Array<{ key: string; value: string }>>([])

function initialize() {
  entries.value = Object.entries(props.modelValue || {}).flatMap(([key, values]) =>
    Array.isArray(values)
      ? values.map((value) => ({ key, value }))
      : [{ key, value: String(values) }],
  )
  if (entries.value.length === 0) entries.value.push({ key: '', value: '' })
}

function emitUpdate() {
  const headers: Record<string, string[]> = {}
  for (const entry of entries.value) {
    const key = entry.key.trim()
    if (!key) continue
    ;(headers[key] ??= []).push(entry.value)
  }
  emit('update:modelValue', headers)
}

function addRow() {
  entries.value.push({ key: '', value: '' })
}

function removeRow(index: number) {
  entries.value.splice(index, 1)
  if (entries.value.length === 0) entries.value.push({ key: '', value: '' })
  emitUpdate()
}

// The connection's headers win at runtime (ADR-030) — a user row reusing one of those names gets
// an inline marker instead of being blocked.
const reservedNames = computed(() => ghostHeaderNames(props.connectionHeaders ?? []))
const isOverridden = (key: string) => reservedNames.value.has(key.trim().toLowerCase())

function ghostTitle(row: GhostHeaderRow): string {
  if (row.value !== null) return `Added by connection '${props.connectionName}' at run time`
  return `Depends on the connection's auth method:\n${row.variants
    .map((v) => `${v.method} → ${v.name}: ${v.value}`)
    .join('\n')}`
}

onMounted(initialize)
</script>

<template>
  <div class="mb-3">
    <label class="form-label">Headers</label>

    <!-- Headers the connection injects at run time: plain rows, disabled, tooltip for provenance.
         The lock button sits where the user rows' delete button does, keeping the columns aligned. -->
    <div
      v-for="row in connectionHeaders"
      :key="row.name"
      class="d-flex align-items-stretch gap-2 mb-2"
      :title="ghostTitle(row)"
    >
      <input type="text" class="form-control w-25 flex-shrink-0 font-monospace" :value="row.name" disabled />
      <input
        type="text"
        class="form-control flex-grow-1 font-monospace"
        :value="row.value ?? 'varies by auth method'"
        :class="{ 'fst-italic': row.value === null }"
        disabled
      />
      <button type="button" class="btn btn-sm" disabled>
        <i class="bi bi-lock-fill"></i>
      </button>
    </div>

    <div
      v-for="(entry, index) in entries"
      :key="index"
      class="d-flex align-items-stretch gap-2 mb-2"
    >
      <BbelField
        v-model="entry.key"
        class="w-25 flex-shrink-0"
        :scope="scope"
        single-line
        placeholder="Header name"
        @update:model-value="emitUpdate"
      />
      <BbelField
        v-model="entry.value"
        class="flex-grow-1"
        :scope="scope"
        single-line
        placeholder="Header value"
        @update:model-value="emitUpdate"
      />
      <i
        v-if="isOverridden(entry.key)"
        class="bi bi-exclamation-triangle text-warning align-self-center"
        title="The connection sets this header at run time — this value will be overridden."
      ></i>
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        @click="removeRow(index)"
        :disabled="entries.length === 1 && !entry.key && !entry.value"
      >
        <i class="bi bi-trash"></i>
      </button>
    </div>
    <button type="button" class="btn btn-outline-primary btn-sm" @click="addRow">
      <i class="bi bi-plus"></i> Add Header
    </button>
  </div>
</template>
